import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { FormModal, RHFTextField, RHFSelect } from '../components/form';
import Typography from '@mui/material/Typography';
import { Container, Table, tableCellClasses, Button, Box, IconButton, Grid, Select, MenuItem, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { fetchSalesCoordinatorNewInstallations, updateSalesCoordinatorNewInstallation, deleteSalesCoordinatorNewInstallation, fetchCustomers, fetchVehicles, fetchSolutions, fetchSalesNewInstallation, fetchCustomerSupportNewInstallation, fetchTechnicalNewInstallation, emailTechnicalNewInstallationPdf } from "../services/ApiService";
import BulkUploadAttachment from '../components/BulkUploadAttachment';
import EditIcon from '@mui/icons-material/Edit';
import GenerateInvoiceButton from '../components/Invoice';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import DataTable from '../components/DataTable';

const editRecordSchema = z.object({
  cost: z.string().optional(),
  billing_date: z.string().min(1, "Required"),
  type: z.string().min(1, "Required"),
  AMCCost: z.string().optional(),
  duration: z.string().optional(),
  discount: z.string().optional(),
  discountedCost: z.string().optional(),
  remarks: z.string().optional(),
}).superRefine((d, ctx) => {
  if (d.type === "Annual" && !d.AMCCost) ctx.addIssue({ path: ["AMCCost"], message: "Required", code: "custom" });
  if (d.type === "Rental" && !d.duration) ctx.addIssue({ path: ["duration"], message: "Required", code: "custom" });
});

const BillingCalculator = () => {
  const { watch, setValue } = useFormContext();
  const cost = watch("cost");
  const AMCCost = watch("AMCCost");
  const discount = watch("discount");
  React.useEffect(() => {
    const base = parseFloat(cost || 0) + parseFloat(AMCCost || 0);
    const discounted = base - base * parseFloat(discount || 0) / 100;
    setValue("discountedCost", isNaN(discounted) ? "" : String(Math.round(discounted)));
  }, [cost, AMCCost, discount, setValue]);
  return null;
};

export function SalesCoordinatorNewInstallation() {
  const [devicesOptions, setDevicesOptions] = React.useState([])
  const [haulier, setHauliers] = React.useState([])
  const [devices, setDevices] = React.useState([])
  const [trucks, setTrucks] = React.useState([])
  const [employees, setEmployees] = React.useState([])
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editModalOn, setEditModalOn] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [selectedSale, setSelectedSale] = React.useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteTargetId, setDeleteTargetId] = React.useState(null);
  const handleDeleteClick = (id) => { setDeleteTargetId(id); setDeleteConfirmOpen(true); };
  const confirmDelete = async () => {
    if (deleteTargetId !== null) await handleDelete(deleteTargetId);
    setDeleteConfirmOpen(false); setDeleteTargetId(null);
  };
  const [saleModalOpen, setSaleModalOpen] = React.useState(false);
  const [selectedCustomerSupport, setSelectedCustomerSupport] = React.useState(null);
  const [csModalOpen, setCSModalOpen] = React.useState(false);
  const [selectedTechnical, setSelectedTechnical] = React.useState(null);
  const [technicalModalOpen, setTechnicalModalOpen] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [haulierOptions, setHaulierOptions] = React.useState([]);
  const [truckOptions, setTruckOptions] = React.useState([]);
  const [documentFile, setDocumentFile] = React.useState(null);

  React.useEffect(() => {
    fetchHauliers();
    fetchDevices()
    fetchTruck();
    fetchDeliveries();
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDateForInput = (datetimeStr) => {
    if (!datetimeStr) return "";
    const date = new Date(datetimeStr);
    return date.toISOString().slice(0, 16);
  };

  const handleViewSaleDetail = async (saleId) => {
    try {
      const response = await fetchSalesNewInstallation(saleId);
      console.log(response.data);
      setSelectedSale(response.data);
      setSaleModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch sale detail:", error);
    }
  };

  const handleViewCustomerSupportDetail = async (saleId) => {
    try {
      const response = await fetchCustomerSupportNewInstallation(saleId);
      console.log(response.data);
      setSelectedCustomerSupport(response.data);
      setCSModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch sale detail:", error);
    }
  };

  const handleViewTechnicalDetail = async (saleId) => {
    try {
      const response = await fetchTechnicalNewInstallation(saleId);
      console.log(response.data);
      setSelectedTechnical(response.data);
      setTechnicalModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch sale detail:", error);
    }
  };

  const fetchDeliveries = async () => {
    try {
      const response = await fetchSalesCoordinatorNewInstallations();
      console.log(response.data);
      setEmployees(response.data);
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
    }
  };

  const fetchHauliers = async () => {
    try {
      const response = await fetchCustomers();
      const haulierDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      const haulier = response.data.reduce((acc, item) => {
        acc[item.id] = item.id;
        return acc;
      }, {});
      setHaulierOptions(haulierDict);
      setHauliers(haulier);
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
      console.error("Error fetching hauliers:", error);
    }
  };

  const handleSendEmail = async (saleId) => {
    try {
      await emailTechnicalNewInstallationPdf(saleId, {});
      alert("Email sent successfully!");
    } catch (error) {
      console.error("Email error:", error);
      alert("Failed to send email.");
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleStatusChange = async (saleId, newStatus, sale) => {
    const updatedSale = { ...sale, status: newStatus };
    try {
      await updateSalesCoordinatorNewInstallation(saleId, updatedSale);
      setEmployees((prevData) =>
        prevData.map((item) =>
          item.id === saleId ? { ...item, status: newStatus } : item
        )
      );
      showSnackbar('Status updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update status:', error);
      showSnackbar('Failed to update status', 'error');
    }
  };

  const fetchTruck = async () => {
    try {
      const response = await fetchVehicles();
      console.log(response.data);
      const allNames = response.data.reduce((acc, item) => {
        acc[item.id] = item.registration_number;
        return acc;
      }, {});
      const haulierDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      setTruckOptions(haulierDict)
      setTrucks(allNames);
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
    }
  };

  const handleEditOn = (row) => {
    setEditModalOn(true)
    setSelectedRow(row)
  }

  const handleEditOff = () => { setEditModalOn(false); setDocumentFile(null); }

  const fetchDevices = async () => {
    try {
      const response = await fetchSolutions();
      const allNames = response.data.map((employee) => employee.name);
      const deviceDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      const device = response.data.reduce((acc, item) => {
        acc[item.id] = item.cost;
        return acc;
      }, {});
      setDevicesOptions(deviceDict)
      setDevices(device);
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
    }
  };

  const handleExport = () => {
    const doc = new jsPDF("l", "mm", "a3");
    doc.setFontSize(18);
    doc.text("Collection Recovery New Installation", 14, 16);
    const columns = [
      "ID", "Sale", "Customer Support", "Discount", "Discounted Cost",
      "Remarks", "Type", "Duration", "Billing Date", "Sale Status",
      "Customer Support Status", "Technical Status", "Overall Status",
    ];
    const tableData = employees.map((row) => [
      row.id, row.sale, row.customer_support, row.discount, row.discountedCost,
      row.remarks, row.type, row.duration, formatDateForInput(row.billing_date),
      row.sale_details?.status || "", row.sale_details?.customer_support_status || "",
      row.technical_status || "", row.status,
    ]);
    doc.autoTable({
      head: [columns], body: tableData, startY: 30,
      styles: { fontSize: 9, cellPadding: 3 }, theme: "grid",
      didDrawPage: () => { doc.setFontSize(18); doc.text("Collection Recovery New Installation Report", 14, 16); },
    });
    doc.save("Collection_Recovery_New_Installation_Report.pdf");
  };

  const EditEmployeeModal = ({ selectedRow, open, handleClose }) => {
    return (
      <FormModal
        open={open}
        onClose={handleClose}
        title="Edit Record"
        schema={editRecordSchema}
        defaultValues={{
          cost: String(selectedRow?.cost ?? ""),
          billing_date: selectedRow?.billing_date ? selectedRow.billing_date.slice(0, 16) : "",
          type: selectedRow?.type ?? "",
          AMCCost: String(selectedRow?.AMCCost ?? ""),
          duration: selectedRow?.duration ?? "",
          discount: String(selectedRow?.discount ?? ""),
          discountedCost: String(selectedRow?.discountedCost ?? selectedRow?.discounted_cost ?? ""),
          remarks: selectedRow?.remarks ?? "",
        }}
        onSubmit={async (data) => {
          const fd = new FormData();
          Object.entries(data).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") fd.append(k, v); });
          if (documentFile) fd.append("document", documentFile);
          try {
            await updateSalesCoordinatorNewInstallation(selectedRow.id, fd);
            showSnackbar("Updated successfully", "success");
            fetchDeliveries();
          } catch {
            showSnackbar("Failed to update", "error");
            throw new Error();
          }
        }}
        submitLabel="Update"
        width={700}
      >
        {({ control, watch }) => {
          const type = watch("type");
          return (
            <Grid container spacing={2.5}>
              <BillingCalculator />
              <Grid item xs={12} md={6}><RHFTextField control={control} name="cost" label="Cost" disabled /></Grid>
              <Grid item xs={12} md={6}><RHFTextField control={control} name="billing_date" label="Billing Date" type="datetime-local" InputLabelProps={{ shrink: true }} required /></Grid>
              <Grid item xs={12} md={6}>
                <RHFSelect control={control} name="type" label="Type" required>
                  <MenuItem value="Annual">Annual</MenuItem>
                  <MenuItem value="Rental">Rental</MenuItem>
                </RHFSelect>
              </Grid>
              {type === "Annual" && (
                <Grid item xs={12} md={6}><RHFTextField control={control} name="AMCCost" label="AMC Cost" /></Grid>
              )}
              {type === "Rental" && (
                <Grid item xs={12} md={6}>
                  <RHFSelect control={control} name="duration" label="Duration" required>
                    <MenuItem value="3 Month">3 Month</MenuItem>
                    <MenuItem value="6 Month">6 Month</MenuItem>
                    <MenuItem value="9 Month">9 Month</MenuItem>
                    <MenuItem value="12 Month">12 Month</MenuItem>
                  </RHFSelect>
                </Grid>
              )}
              <Grid item xs={12} md={6}><RHFTextField control={control} name="discount" label="Discount (%)" type="number" /></Grid>
              <Grid item xs={12} md={6}><RHFTextField control={control} name="discountedCost" label="Discounted Cost" disabled /></Grid>
              <Grid item xs={12}><RHFTextField control={control} name="remarks" label="Remarks" multiline rows={3} /></Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>Document (optional)</Typography>
                <input type="file" accept=".pdf,.doc,.docx,.txt,image/*" style={{ width: "100%" }} onChange={(e) => setDocumentFile(e.target.files?.[0] ?? null)} />
              </Grid>
            </Grid>
          );
        }}
      </FormModal>
    );
  };

  const handleDelete = async (id) => {
    try {
      await deleteSalesCoordinatorNewInstallation(id);
      showSnackbar("Customer deleted successfully", "success");
    } catch (error) {
      showSnackbar("Failed to delete customer", "error");
      console.error(error);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ m: 2 }}>
      {editModalOn && selectedRow && (
        <EditEmployeeModal
          selectedRow={selectedRow}
          open={editModalOn}
          handleClose={handleEditOff}
        />
      )}
      <DataTable
        title="Collection Recovery New Installation List"
        columns={[
          { key: 'id', label: 'Attachment #' },
          { key: 'sale', label: 'Sale #', render: (row) => (
            <span onClick={() => handleViewSaleDetail(row.sale)} style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}>{row.sale}</span>
          )},
          { key: 'customer_support', label: 'CS #', render: (row) => (
            <span onClick={() => handleViewCustomerSupportDetail(row.customer_support)} style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}>{row.customer_support}</span>
          )},
          { key: 'discount', label: 'Discount' },
          { key: 'discountedCost', label: 'Discounted Cost' },
          { key: 'remarks', label: 'Remarks' },
          { key: 'type', label: 'Type' },
          { key: 'duration', label: 'Duration' },
          { key: 'billing_date', label: 'Billing Date', render: (row) => formatDateForInput(row.billing_date) },
          { key: 'sale_status', label: 'Sale Status', render: (row) => row.sale_details?.status },
          { key: 'cs_status', label: 'CS Status', render: (row) => row.sale_details?.customer_support_status },
          { key: 'technical_status', label: 'Technical Status' },
        ]}
        rows={employees}
        toolbarActions={<Button variant="contained" color="primary" style={{ marginLeft: "10px" }} onClick={handleExport}>Export to PDF</Button>}
        renderActions={(row) => (
          <Box display="flex" gap="6px">
            <IconButton color="secondary" size="small" onClick={() => handleEditOn(row)} sx={{ padding: '6px' }} disabled={row.status === 'Invoice Sent'}><EditIcon /></IconButton>
            <Select fullWidth value={row.status} onChange={(e) => handleStatusChange(row.id, e.target.value, row)} size="small" sx={{ ml: 1, minWidth: 100 }}>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Invoice Sent">Invoice Sent</MenuItem>
            </Select>
            <GenerateInvoiceButton saleId={row.id} isRemovalTransfer={false} />
            <IconButton color="error" size="small" onClick={() => handleDeleteClick(row.id)} sx={{ padding: '6px', marginLeft: '4px' }} disabled={row.status === 'Invoice Sent'}><DeleteIcon /></IconButton>
          </Box>
        )}
      />
      <Dialog open={saleModalOpen} onClose={() => setSaleModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sale Details</DialogTitle>
        <DialogContent>
          {selectedSale ? (
            <Box>
              <Typography variant="body1"><strong>Customer:</strong> {haulierOptions[selectedSale.customer]}</Typography>
              <Typography variant="body1"><strong>Solution:</strong> {devicesOptions[selectedSale.solution]}</Typography>
              <Typography variant="body1"><strong>Corporate:</strong> {selectedSale.corporate}</Typography>
              <Typography variant="body1"><strong>POC Name:</strong> {selectedSale.poc_name}</Typography>
              <Typography variant="body1"><strong>POC Contact:</strong> {selectedSale.poc_contact}</Typography>
              <Typography variant="body1"><strong>Plan:</strong> {selectedSale.plan}</Typography>
              <Typography variant="body1"><strong>Unit:</strong> {selectedSale.unit}</Typography>
            </Box>
          ) : (
            <Typography variant="body2">Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaleModalOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={csModalOpen} onClose={() => setCSModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Customer Support Details</DialogTitle>
        <DialogContent>
          {selectedCustomerSupport ? (
            <Box>
              <Typography variant="body1"><strong>Vehicle:</strong> {selectedCustomerSupport.vehicle_details?.name}</Typography>
              <Typography variant="body1"><strong>Location:</strong> {selectedCustomerSupport.location}</Typography>
            </Box>
          ) : (
            <Typography variant="body2">Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCSModalOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={technicalModalOpen} onClose={() => setTechnicalModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Technical Details</DialogTitle>
        <DialogContent>
          {selectedTechnical ? (
            <Box>
              <Typography variant="body1"><strong>IMEI:</strong> {selectedTechnical.imei_details?.imei}</Typography>
            </Box>
          ) : (
            <Typography variant="body2">Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTechnicalModalOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
